import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  pdf,
} from "@react-pdf/renderer";
import dayjs from "@lib/dayjs";

// Define styles
const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 10,
    fontFamily: "Helvetica",
    backgroundColor: "#FFFFFF",
  },
  header: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 40,
    paddingBottom: 10,
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    textAlign: "center",
  },
  companySection: {
    marginBottom: 40,
  },
  companyHeader: {
    flexDirection: "row",
    marginBottom: 10,
    alignItems: "flex-end",
  },
  companyLogo: {
    fontSize: 28,
    fontWeight: "300",
    color: "#2E2E2E",
    lineHeight: 1,
  },
  companyName: {
    fontSize: 18,
    fontWeight: "300",
    color: "#2E2E2E",
    letterSpacing: 2.8,
    marginLeft: 4,
    lineHeight: 1,
    marginBottom: 2,
  },
  companyAddress: {
    fontSize: 10,
    lineHeight: 1.6,
    marginTop: 5,
  },
  mainGrid: {
    flexDirection: "row",
    marginBottom: 30,
  },
  leftColumn: {
    width: "48%",
    paddingRight: 20,
  },
  rightColumn: {
    width: "52%",
    paddingLeft: 20,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#E5E5E5",
    paddingBottom: 5,
  },
  detailsGrid: {
    marginBottom: 25,
  },
  detailRow: {
    flexDirection: "row",
    marginBottom: 8,
    alignItems: "flex-start",
  },
  detailLabel: {
    width: "40%",
    fontSize: 10,
    color: "#333333",
  },
  detailValue: {
    width: "60%",
    fontSize: 10,
    fontWeight: "500",
    color: "#000000",
  },
  linkText: {
    color: "#03A9F4",
  },
  successText: {
    color: "#16A34A",
  },
  paymentSection: {
    marginTop: 20,
  },
  paymentRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 8,
    paddingBottom: 4,
  },
  paymentLabel: {
    fontSize: 10,
    color: "#333333",
  },
  paymentValue: {
    fontSize: 10,
    fontWeight: "500",
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 15,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: "#E5E5E5",
    marginBottom: 15,
  },
  totalLabel: {
    fontSize: 11,
    fontWeight: "bold",
  },
  totalValue: {
    fontSize: 11,
    fontWeight: "bold",
  },
  paidSection: {
    backgroundColor: "#F1F5F9",
    padding: 12,
    borderRadius: 4,
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 20,
    marginBottom: 15,
  },
  paidText: {
    fontSize: 10,
  },
  paidAmount: {
    fontSize: 10,
    fontWeight: "500",
  },
  paidDate: {
    fontSize: 10,
    fontWeight: "500",
  },
  paymentMethod: {
    flexDirection: "row",
    marginTop: 15,
    alignItems: "center",
  },
  paymentMethodLabel: {
    fontSize: 10,
    marginRight: 10,
  },
  paymentMethodValue: {
    fontSize: 10,
    fontWeight: "500",
  },
  receivedFromSection: {
    marginBottom: 30,
  },
  addressText: {
    fontSize: 10,
    lineHeight: 1.6,
    marginBottom: 15,
  },
  referenceGrid: {
    marginTop: 15,
  },
});

// PDF Document Component
const TransactionPDFDocument = ({ paymentHistoryDetails, type }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.title}>RECEIPT</Text>
      </View>

      {/* Company and Received From Section */}
      <View style={styles.mainGrid}>
        {/* Left Column - Company Info */}
        <View style={styles.leftColumn}>
          <View style={styles.companySection}>
            <View style={styles.companyHeader}>
              <Text style={styles.companyLogo}>C</Text>
              <Text style={styles.companyName}>OMTRACKED</Text>
            </View>
            <View style={styles.companyAddress}>
              <Text>COMTRACKED LTD</Text>
              <Text>128 City Road</Text>
              <Text>London</Text>
              <Text>EC1v 2NX</Text>
              <Text>United Kingdom</Text>
            </View>
          </View>
        </View>

        {/* Right Column - Received From */}
        <View style={styles.rightColumn}>
          <View style={styles.receivedFromSection}>
            <Text style={styles.sectionTitle}>Received from</Text>
            <View style={styles.addressText}>
              <Text>ABC Recruitment Ltd.</Text>
              <Text>12 High Road</Text>
              <Text>London</Text>
              <Text>NW10 5RJ</Text>
              <Text>United Kingdom</Text>
            </View>
            <View style={styles.referenceGrid}>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Member reference:</Text>
                <Text style={styles.detailValue}>
                  {paymentHistoryDetails?.recruiterDetails?.refNo || "NA"}
                </Text>
              </View>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Date:</Text>
                <Text style={styles.detailValue}>
                  {paymentHistoryDetails?.createdAt
                    ? dayjs(paymentHistoryDetails.createdAt).format(
                        "Do MMMM, YYYY"
                      )
                    : "NA"}
                </Text>
              </View>
            </View>
          </View>
        </View>
      </View>

      {/* Main Content Grid */}
      <View style={styles.mainGrid}>
        {/* Left Column - Details Sections */}
        <View style={styles.leftColumn}>
          {/* Referral Payment Type */}
          {type === "referralpayment" && (
            <View style={styles.detailsGrid}>
              <Text style={styles.sectionTitle}>Referral fee recipient</Text>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Reference:</Text>
                <Text style={styles.detailValue}>
                  {paymentHistoryDetails?.referralDetails?.refNo || "NA"}
                </Text>
              </View>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Name:</Text>
                <Text style={styles.detailValue}>
                  {`${
                    paymentHistoryDetails?.referralDetails?.firstName || "NA"
                  } ${
                    paymentHistoryDetails?.referralDetails?.lastName || "NA"
                  }`}
                </Text>
              </View>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Username:</Text>
                <Text style={[styles.detailValue, styles.linkText]}>
                  {paymentHistoryDetails?.referralDetails?.userName || "NA"}
                </Text>
              </View>
            </View>
          )}

          {/* Subscription Type */}
          {type === "subscription" && (
            <View style={styles.detailsGrid}>
              <Text style={styles.sectionTitle}>Subscription Details</Text>
              <Text style={[styles.detailValue, { marginBottom: 8 }]}>
                Premium membership
              </Text>
              <Text style={styles.detailValue}>
                {paymentHistoryDetails?.subscriptionDetails?.type === "month"
                  ? "Monthly"
                  : "Yearly"}{" "}
                billing
              </Text>
            </View>
          )}

          {/* Job Details for both referralpayment and jobpost */}
          {(type === "jobpost" || type === "referralpayment") && (
            <View style={styles.detailsGrid}>
              <Text style={styles.sectionTitle}>
                Job {type === "jobpost" ? "Post" : "Details"}
              </Text>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Reference:</Text>
                <Text style={styles.detailValue}>
                  {paymentHistoryDetails?.jobDetails?.refNo || "NA"}
                </Text>
              </View>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Title:</Text>
                <Text style={styles.detailValue}>
                  {paymentHistoryDetails?.jobDetails?.title || "NA"}
                </Text>
              </View>
            </View>
          )}

          {/* Candidate Placed - only for referralpayment */}
          {type === "referralpayment" && (
            <View style={styles.detailsGrid}>
              <Text style={styles.sectionTitle}>Candidate Placed</Text>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Reference:</Text>
                <Text style={styles.detailValue}>
                  {paymentHistoryDetails?.candidateDetails?.refNo || "NA"}
                </Text>
              </View>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Name:</Text>
                <Text style={styles.detailValue}>
                  {`${
                    paymentHistoryDetails?.candidateDetails?.firstName || "NA"
                  } ${paymentHistoryDetails?.candidateDetails?.lastName || ""}`}
                </Text>
              </View>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Username:</Text>
                <Text style={[styles.detailValue, styles.linkText]}>
                  {paymentHistoryDetails?.candidateDetails?.userName || "NA"}
                </Text>
              </View>
            </View>
          )}

          {/* Posted by - only for jobpost */}
          {type === "jobpost" && (
            <View style={styles.detailsGrid}>
              <Text style={styles.sectionTitle}>Posted by</Text>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Name:</Text>
                <Text style={styles.detailValue}>
                  {`${
                    paymentHistoryDetails?.recruiterDetails?.firstName || "NA"
                  } ${paymentHistoryDetails?.recruiterDetails?.lastName || ""}`}
                </Text>
              </View>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Reference:</Text>
                <Text style={styles.detailValue}>
                  {paymentHistoryDetails?.recruiterDetails?.refNo || "NA"}
                </Text>
              </View>
            </View>
          )}
        </View>

        {/* Right Column - Payment Details */}
        <View style={styles.rightColumn}>
          <View style={styles.paymentSection}>
            <Text style={styles.sectionTitle}>Payment Details</Text>

            {/* Payment Fees */}
            <View style={styles.paymentRow}>
              <Text style={styles.paymentLabel}>
                {type === "referralpayment" ? "Referral fee" : "Membership fee"}
                :
              </Text>
              <Text style={styles.paymentValue}>
                £{" "}
                {type === "referralpayment" &&
                paymentHistoryDetails?.jobDetails?.referralAmount
                  ? paymentHistoryDetails.jobDetails.referralAmount.toFixed(2)
                  : paymentHistoryDetails?.totalAmount
                  ? paymentHistoryDetails.totalAmount.toFixed(2)
                  : "NA"}
              </Text>
            </View>

            {/* Commission fee - only for referralpayment */}
            {type === "referralpayment" && (
              <View style={styles.paymentRow}>
                <Text style={styles.paymentLabel}>Commission fee:</Text>
                <Text style={styles.paymentValue}>
                  £{" "}
                  {paymentHistoryDetails?.totalAmount &&
                  paymentHistoryDetails?.jobDetails?.referralAmount
                    ? (
                        paymentHistoryDetails.totalAmount -
                        paymentHistoryDetails.jobDetails.referralAmount
                      ).toFixed(2)
                    : "NA"}
                </Text>
              </View>
            )}

            {/* Total */}
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Total:</Text>
              <Text style={styles.totalValue}>
                £ {paymentHistoryDetails?.totalAmount?.toFixed(2) || "0.00"}
              </Text>
            </View>

            {/* Paid Section */}
            <View style={styles.paidSection}>
              <Text style={styles.paidText}>Paid</Text>
              <Text style={styles.paidAmount}>
                £ {paymentHistoryDetails?.totalAmount?.toFixed(2) || "NA"}
              </Text>
              <Text style={styles.paidDate}>
                {paymentHistoryDetails?.createdAt
                  ? dayjs(paymentHistoryDetails.createdAt).format(
                      "HH:mm DD/MM/YYYY"
                    )
                  : "NA"}
              </Text>
            </View>

            {/* Payment Method */}
            <View style={styles.paymentMethod}>
              <Text style={styles.paymentMethodLabel}>Payment method:</Text>
              <Text style={styles.paymentMethodValue}>
                Card ending -{" "}
                {paymentHistoryDetails?.last4Digit ||
                  paymentHistoryDetails?.paymentMethodDetails?.last4 ||
                  "NA"}
              </Text>
            </View>
          </View>
        </View>
      </View>
    </Page>
  </Document>
);

// Function to generate and download PDF
export const generateTransactionPDF = async (paymentHistoryDetails, type) => {
  try {
    const blob = await pdf(
      <TransactionPDFDocument
        paymentHistoryDetails={paymentHistoryDetails}
        type={type}
      />
    ).toBlob();

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `transaction-${type}-${
      paymentHistoryDetails?.id || Date.now()
    }.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return true;
  } catch (error) {
    console.error("Error generating PDF:", error);
    return false;
  }
};

export default TransactionPDFDocument;
